import { type ClassValue, clsx } from "clsx";
import { setHours, setMinutes } from "date-fns";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type Nullable<T> = {
  [K in keyof T]: T[K] extends object ? Nullable<T[K]> : T[K] | null;
};

/**
 * Transforms empty strings to null values in an object.
 * Passing a null value to an API endpoint will remove the key from the object.
 * @param obj - The object to transform.
 * @returns The transformed object.
 */
export function transformEmptyValuesToNull<T extends object>(
  obj: T,
): Nullable<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = Array.isArray(obj) ? [] : {};

  for (const key in obj) {
    const value = obj[key];

    if (typeof value === "object" && value !== null) {
      result[key] = transformEmptyValuesToNull(value);
    } else if (typeof value === "string" && value.trim() === "") {
      result[key] = null;
    } else {
      result[key] = value;
    }
  }

  return result;
}

export function getDateWithTime(date: Date, time: Date): Date {
  return setMinutes(setHours(date, time.getHours()), time.getMinutes());
}

export async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    // Optionally add a toast notification here
  } catch (err) {
    console.error("Failed to copy text: ", err);
  }
}
